import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const cardVariants = cva(
  'flex flex-col gap-6 rounded-xl border py-6 text-card-foreground shadow-sm transition-colors',
  {
    variants: {
      variant: {
        default: 'border-border/80 bg-card dark:border-border/60',
        glass: 'glass rounded-2xl overflow-hidden text-foreground shadow-lg shadow-primary/10',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

type CardProps = React.ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof cardVariants> & {
    opaque?: boolean
    staticBlur?: boolean
  }

const Card = React.forwardRef<React.ElementRef<'div'>, CardProps>(
  ({ className, variant, opaque, staticBlur, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="card"
        data-variant={variant ?? undefined}
        data-opaque={opaque ? 'true' : undefined}
        data-static={staticBlur ? 'true' : undefined}
        className={cn(cardVariants({ variant }), className)}
        {...props}
      />
    )
  }
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card-header"
      className={cn(
        '@container/card-header [.border-b]:pb-6 grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-[&>[data-slot=card-action]]:grid-cols-[1fr_auto]',
        className
      )}
      {...props}
    />
  )
)
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card-title"
      className={cn('font-semibold leading-none', className)}
      {...props}
    />
  )
)
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
)
CardDescription.displayName = 'CardDescription'

const CardAction = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  )
)
CardAction.displayName = 'CardAction'

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} data-slot="card-content" className={cn('px-6', className)} {...props} />
  )
)
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card-footer"
      className={cn('[.border-t]:pt-6 flex items-center px-6', className)}
      {...props}
    />
  )
)
CardFooter.displayName = 'CardFooter'

/**
 * Card exports. `Card` supports `variant` values: `"default"` (compatible) and `"glass"` (frosted overlay).
 */
export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  cardVariants,
}

export type { CardProps }
